/**
 * EN/AR strings for the KMD editor panel, local to the khiyarsa layer (same
 * pattern as markdown/ui/strings.ts and the cloud module).
 */
import { useAppLangCode } from "../../../app-language/language-state";

const en = {
  openEditor: "Diagram Editor…",
  openInEditor: "Open in Editor",
  panelTitle: "Diagram Editor",
  placeholder:
    "Write Khiyarsa Markdown here. The canvas updates as you type.\n\n---\nkhiyarsa: 1\ntype: flow\n---\n\n## First stage\n- Step one",
  close: "Close",
  done: "Done",
  clear: "Clear",
  fitDiagram: "Fit diagram in view",
  line: "line",
  state_idle: "Not rendering",
  state_bound: "Live",
  state_detached: "Detached",
  detached:
    "This diagram is now edited on canvas. The text no longer drives it.",
  rerenderFromText: "Re-render from text",
  recoverPrompt: "Unsaved editor text was found from a previous session.",
  recoverRestore: "Restore it",
  recoverDiscard: "Discard",

  // ---- button rail --------------------------------------------------------
  btn_container_flow: "Stage",
  btn_container_swimlane: "Lane",
  btn_container_architecture: "Zone",
  btn_container_sequence: "Participant",
  btn_container_mindmap: "Branch",
  btn_container_tree: "Branch",
  btn_container_er: "Entity",
  btn_container_timeline: "Period",
  btn_subcontainer: "Subsystem",
  btn_node: "Step",
  btn_child: "Child",
  btn_child_sibling: "Item",
  btn_field: "Field",
  btn_event: "Event",
  btn_decision: "Decision",
  btn_branch: "Branch",
  btn_start: "Start",
  btn_end: "End",
  btn_edge: "Arrow",
  btn_crosslink: "Cross-link",
  btn_message: "Message",
  btn_reply: "Reply",
  btn_self: "Self-message",
  btn_relationship: "Relationship",
  btn_sectionBreak: "Section break",
  btn_group: "Group",
  btn_loop: "Loop",
  btn_alt: "Alternatives",
  btn_opt: "Optional",
  btn_par: "Parallel",
  btn_note: "Note",
  newDiagram: "New diagram",
  family_flow: "Flowchart",
  family_swimlane: "Swimlanes",
  family_architecture: "Architecture",
  family_sequence: "Sequence",
  family_mindmap: "Mind map",
  family_tree: "Tree",
  family_er: "ER diagram",
  family_timeline: "Timeline",

  // ---- style panel --------------------------------------------------------
  tabWrite: "Write",
  tabStyle: "Style",
  styleDocument: "Whole diagram",
  styleSelection: "Selected line",
  stylePreset: "Template",
  preset_blueprint: "Blueprint",
  preset_sketch: "Sketch",
  preset_poster: "Poster",
  preset_docs: "Docs",
  fm_theme: "Theme",
  fm_spacing: "Spacing",
  fm_direction: "Direction",
  fm_routing: "Routing",
  style_kind: "Kind",
  style_tags: "Tags",
  style_fill: "Fill",
  style_stroke: "Stroke",
  "style_text-color": "Text colour",
  "style_fill-pattern": "Fill pattern",
  style_line: "Line style",
  style_width: "Stroke width",
  style_sketch: "Sketchiness",
  style_rounded: "Rounded",
  style_opacity: "Opacity",
  style_font: "Font",
  "style_font-size": "Font size",
  "style_text-align": "Text align",
  style_shape: "Shape",
  customColor: "Exact colour",
  badHex: "Use #RGB, #RGBA, #RRGGBB or #RRGGBBAA",
  clearValue: "Clear",
  pick: "Pick…",
  kind_node: "node",
  kind_container: "container",
  kind_edge: "arrow",
  kind_field: "field",
  kind_note: "note",
  kind_directive: "frame",
  why_nodesOnly: "Only applies to nodes",
  why_rectOnly: "Only applies to rectangles",
  why_noTarget: "Put the cursor on a line to style it",
  why_notAKey: "Not supported",
  why_directionIgnored: "This diagram type ignores direction",
  why_routingArchOnly: "Only architecture diagrams use routing",
  gateExperimental:
    "Architecture diagrams are experimental here — layout is still being improved, so expect rough edges while you type.",
  gateBlocked:
    "Swimlane diagrams are not rendered live yet: their cross-lane routing is still being rebuilt. Diagnostics still work, and Import Markdown… inserts the diagram as usual.",
};

const ar: typeof en = {
  openEditor: "محرّر المخططات…",
  openInEditor: "فتح في المحرّر",
  panelTitle: "محرّر المخططات",
  placeholder:
    "اكتب خيارسة ماركداون هنا. تتحدّث اللوحة أثناء الكتابة.\n\n---\nkhiyarsa: 1\ntype: flow\n---\n\n## المرحلة الأولى\n- الخطوة الأولى",
  close: "إغلاق",
  done: "تم",
  clear: "مسح",
  fitDiagram: "ملاءمة المخطط في العرض",
  line: "سطر",
  state_idle: "لا يوجد عرض",
  state_bound: "مباشر",
  state_detached: "منفصل",
  detached: "يُحرَّر هذا المخطط الآن على اللوحة. لم يعد النص يتحكم به.",
  rerenderFromText: "إعادة العرض من النص",
  recoverPrompt: "تم العثور على نص غير محفوظ من جلسة سابقة.",
  recoverRestore: "استعادته",
  recoverDiscard: "تجاهل",

  // ---- شريط الأزرار -------------------------------------------------------
  btn_container_flow: "مرحلة",
  btn_container_swimlane: "مسار",
  btn_container_architecture: "منطقة",
  btn_container_sequence: "مشارك",
  btn_container_mindmap: "فرع",
  btn_container_tree: "فرع",
  btn_container_er: "كيان",
  btn_container_timeline: "فترة",
  btn_subcontainer: "نظام فرعي",
  btn_node: "خطوة",
  btn_child: "عنصر فرعي",
  btn_child_sibling: "عنصر",
  btn_field: "حقل",
  btn_event: "حدث",
  btn_decision: "قرار",
  btn_branch: "فرع",
  btn_start: "بداية",
  btn_end: "نهاية",
  btn_edge: "سهم",
  btn_crosslink: "رابط عرضي",
  btn_message: "رسالة",
  btn_reply: "رد",
  btn_self: "رسالة ذاتية",
  btn_relationship: "علاقة",
  btn_sectionBreak: "فاصل مقاطع",
  btn_group: "مجموعة",
  btn_loop: "حلقة",
  btn_alt: "بدائل",
  btn_opt: "اختياري",
  btn_par: "متوازٍ",
  btn_note: "ملاحظة",
  newDiagram: "مخطط جديد",
  family_flow: "مخطط انسيابي",
  family_swimlane: "مسارات",
  family_architecture: "معمارية",
  family_sequence: "تسلسل",
  family_mindmap: "خريطة ذهنية",
  family_tree: "شجرة",
  family_er: "مخطط كيانات",
  family_timeline: "خط زمني",

  // ---- لوحة التنسيق -------------------------------------------------------
  tabWrite: "كتابة",
  tabStyle: "تنسيق",
  styleDocument: "المخطط كامل",
  styleSelection: "السطر المحدد",
  stylePreset: "قالب",
  preset_blueprint: "مخطط هندسي",
  preset_sketch: "رسم يدوي",
  preset_poster: "ملصق",
  preset_docs: "مستندات",
  fm_theme: "السمة",
  fm_spacing: "التباعد",
  fm_direction: "الاتجاه",
  fm_routing: "توجيه الأسهم",
  style_kind: "النوع",
  style_tags: "الوسوم",
  style_fill: "التعبئة",
  style_stroke: "الحد",
  "style_text-color": "لون النص",
  "style_fill-pattern": "نمط التعبئة",
  style_line: "نمط الخط",
  style_width: "سماكة الحد",
  style_sketch: "درجة الرسم اليدوي",
  style_rounded: "زوايا دائرية",
  style_opacity: "الشفافية",
  style_font: "الخط",
  "style_font-size": "حجم الخط",
  "style_text-align": "محاذاة النص",
  style_shape: "الشكل",
  customColor: "لون مخصص",
  badHex: "استخدم ‎#RGB أو ‎#RGBA أو ‎#RRGGBB أو ‎#RRGGBBAA",
  clearValue: "مسح",
  pick: "اختر…",
  kind_node: "عنصر",
  kind_container: "حاوية",
  kind_edge: "سهم",
  kind_field: "حقل",
  kind_note: "ملاحظة",
  kind_directive: "إطار",
  why_nodesOnly: "ينطبق على العناصر فقط",
  why_rectOnly: "ينطبق على المستطيلات فقط",
  why_noTarget: "ضع المؤشر على سطر لتنسيقه",
  why_notAKey: "غير مدعوم",
  why_directionIgnored: "هذا النوع من المخططات يتجاهل الاتجاه",
  why_routingArchOnly: "التوجيه يخص مخططات المعمارية فقط",
  gateExperimental:
    "مخططات المعمارية تجريبية هنا — لا يزال التخطيط قيد التحسين، فتوقّع بعض العيوب أثناء الكتابة.",
  gateBlocked:
    "لا تُعرض مخططات المسارات مباشرةً بعد: توجيه الأسهم بين المسارات قيد إعادة البناء. تعمل الملاحظات كالمعتاد، ويمكن إدراج المخطط عبر «استيراد ماركداون…».",
};

export const useEditorT = () => {
  const [langCode] = useAppLangCode();
  const dict = langCode.startsWith("ar") ? ar : en;
  return (key: keyof typeof en) => dict[key];
};
